import { useMemo, useState } from "react";
import { useApp } from "@/store/AppStore";
import SectionTitle from "@/components/base/SectionTitle";
import SalesStats from "@/pages/admin/ventas/components/SalesStats";
import SalesTable from "@/pages/admin/ventas/components/SalesTable";
import SaleFormModal, {
  type SaleFormOutput,
} from "@/pages/admin/ventas/components/SaleFormModal";
import ProfitDistribution from "@/pages/admin/ventas/components/ProfitDistribution";
import AmwayLevelTable from "@/pages/admin/ventas/components/AmwayLevelTable";
import CrmPanel from "@/pages/admin/ventas/components/CrmPanel";
import { saleRealProfit, saleMatchesPeriod } from "@/utils/kpi";
import { pvForPeriod } from "@/utils/pv";
import { monthLabel } from "@/utils/format";
import type { InventoryItem, SaleOrigin, SaleType } from "@/types";

const TYPE_FILTERS: ("Todos" | SaleType)[] = ["Todos", "Full", "Consignación", "A Costo"];
const ORIGIN_FILTERS: ("Todos" | SaleOrigin)[] = ["Todos", "Facebook", "Google", "TikTok", "Casa", "Recompra"];

const SalesPage = () => {
  const { data, addSale, addClient, setInventory } = useApp();
  const [query, setQuery] = useState("");
  const [type, setType] = useState<"Todos" | SaleType>("Todos");
  const [origin, setOrigin] = useState<"Todos" | SaleOrigin>("Todos");
  const [formOpen, setFormOpen] = useState(false);
  const [feedback, setFeedback] = useState("");

  const periodSales = useMemo(
    () => data.sales.filter((s) => saleMatchesPeriod(s, data.period)),
    [data.sales, data.period]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return periodSales.filter((s) => {
      const matchType = type === "Todos" || s.type === type;
      const matchOrigin = origin === "Todos" || s.origin === origin;
      const matchQuery =
        !q ||
        s.clientName.toLowerCase().includes(q) ||
        s.productName.toLowerCase().includes(q);
      return matchType && matchOrigin && matchQuery;
    });
  }, [periodSales, query, type, origin]);

  const grossProfit = periodSales.reduce((acc, s) => acc + saleRealProfit(s, data), 0);
  // PV del período: sale de las compras de stock (no de las ventas) + referidos.
  const totalPv = pvForPeriod(data, data.period).qualifyingPv;

  const handleSubmit = async (out: SaleFormOutput) => {
    let clientId = out.sale.clientId;
    if (out.newClient) {
      const savedClient = await addClient(out.newClient);
      if (!savedClient) {
        setFeedback("No se pudo crear el cliente. Revisa los datos e intenta de nuevo.");
        return;
      }
      clientId = savedClient.id;
    }

    const savedSale = await addSale({ ...out.sale, clientId });
    if (!savedSale) {
      setFeedback("No se pudo registrar la venta. Revisa los datos e intenta de nuevo.");
      return;
    }

    const existing = data.inventory.find((i) => i.productId === out.sale.productId);
    const base: InventoryItem = existing ?? {
      productId: out.sale.productId,
      stockCasa: 0,
      stockPuntoClear: 0,
      stockByAdvisor: {},
      stockOwnedByAdvisor: {},
    };
    const next: InventoryItem = { ...base, stockByAdvisor: { ...base.stockByAdvisor } };
    const q = out.sale.qty;
    if (out.source === "casa") next.stockCasa = Math.max(0, next.stockCasa - q);
    else if (out.source === "clear") next.stockPuntoClear = Math.max(0, next.stockPuntoClear - q);
    else next.stockByAdvisor[out.source] = Math.max(0, (next.stockByAdvisor[out.source] ?? 0) - q);
    await setInventory(next);

    setFormOpen(false);
    setFeedback("Venta registrada y stock actualizado.");
    setTimeout(() => setFeedback(""), 2400);
  };

  return (
    <div className="pb-24">
      <SectionTitle
        text="Ventas, CRM y Liquidación de Utilidades"
        subtitle={`Registro comercial del período ${monthLabel(data.period.month)} ${data.period.year}: cliente, producto, PV, tipo y origen, con reparto automático del 25%.`}
      />

      <SalesStats sales={periodSales} pv={totalPv} />

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mt-5 mb-4">
        <div className="relative flex-1 max-w-md">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-accent-300">
            <i className="ri-search-line" />
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por cliente o producto..."
            className="w-full bg-background-100/70 border border-background-300/50 rounded-md pl-9 pr-3 py-2 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/50 focus:ring-2 focus:ring-accent-500/25"
            aria-label="Buscar venta"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {TYPE_FILTERS.map((t) => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border cursor-pointer whitespace-nowrap transition-colors ${
                type === t
                  ? "bg-accent-500/20 border-accent-500/40 text-accent-200"
                  : "border-background-300/50 text-foreground-600 hover:border-accent-500/30"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <button
          onClick={() => setFormOpen(true)}
          className="px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 lg:ml-auto"
        >
          <i className="ri-add-line" /> Nueva venta
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-[11px] text-foreground-500">Origen:</span>
        {ORIGIN_FILTERS.map((o) => (
          <button
            key={o}
            onClick={() => setOrigin(o)}
            className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border cursor-pointer whitespace-nowrap transition-colors ${
              origin === o
                ? "bg-secondary-500/25 border-secondary-500/40 text-secondary-100"
                : "border-background-300/50 text-foreground-600 hover:border-secondary-500/30"
            }`}
          >
            {o}
          </button>
        ))}
      </div>

      {feedback && (
        <div className="mb-3 rounded-md glass-panel border-accent-500/30 px-4 py-2.5 flex items-center gap-2 animate-fade-up">
          <i className="ri-checkbox-circle-line text-accent-300" />
          <p className="text-xs text-foreground-800">{feedback}</p>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <SalesTable sales={filtered} distributors={data.distributors} />
        </div>
        <div className="space-y-4">
          <ProfitDistribution grossProfit={grossProfit} distribution={data.settings.distribution} />
          <AmwayLevelTable pv={totalPv} />
        </div>
      </div>

      <div className="mt-4">
        <CrmPanel sales={periodSales} />
      </div>

      <SaleFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
        products={data.products}
        clients={data.clients}
        distributors={data.distributors}
      />
    </div>
  );
};

export default SalesPage;